"use client";

import React from "react";
import DynamicForm from "@/components/general/DynamicForm";
import RichText from "@/components/general/nsc/RichText";
import { createFormulierInzending } from "@/lib/api/formulier-inzending";
import { getBgColor, getTextColorByBgColor } from "@/lib/utils";
import type { FormulierSectionBlock, FormulierVeld } from "@/lib/types";

// Backgrounds that need white form text.
const DARK_BACKGROUNDS = [
  "nsc-dark-blue",
  "nsc-europe-blue",
  "nsc-dark-brown",
  "jsc-dark-gray",
  "black",
];

// Map a CMS field type to a type DynamicForm can render.
// DynamicForm has no "number" case, so render it as a text input.
const toFormType = (type: string) => (type === "number" ? "text" : type);

// Normalise the JSON `options` value into DynamicForm's [{ id, label }] shape.
const parseOptions = (raw: any): Array<{ id: string; label: string }> => {
  if (!raw) return [];

  let list = raw;
  if (typeof raw === "string") {
    try {
      list = JSON.parse(raw);
    } catch {
      // Fallback: comma-separated string.
      list = raw.split(",").map((s: string) => s.trim());
    }
  }

  if (!Array.isArray(list)) return [];

  return list
    .map((option: any) => {
      if (option == null) return null;
      if (typeof option === "string") {
        return { id: option, label: option };
      }
      const id = option.value ?? option.id ?? option.label;
      const label = option.label ?? option.value ?? option.id;
      if (id == null) return null;
      return { id: String(id), label: String(label) };
    })
    .filter(Boolean) as Array<{ id: string; label: string }>;
};

const fieldKeyFor = (field: FormulierVeld) => field.fieldKey || field.label;

const FormulierSection = ({ data }: { data: FormulierSectionBlock }) => {
  const formulier = data?.formulier?.data;
  if (!formulier) return null;

  const form = formulier.attributes;
  const fields = form.fields ?? [];

  const backgroundColor = data.backgroundColor || form.backgroundColor || "white";
  const isDarkBg = DARK_BACKGROUNDS.includes(backgroundColor);

  const dynamicFields = fields.map((field) => {
    const id = fieldKeyFor(field);
    const isOptionType = field.type === "select" || field.type === "radio";
    return {
      id,
      label: field.isRequired ? `${field.label}*` : field.label,
      type: toFormType(field.type),
      required: field.isRequired,
      placeholder: field.placeholder ?? undefined,
      extraInfo: field.extraInformation ?? undefined,
      errorMessage: `${field.label} is verplicht in te vullen`,
      ...(isOptionType ? { options: parseOptions(field.options) } : {}),
    };
  });

  const handleSubmit = async (formData: FormData) => {
    const values: Record<string, any> = {};
    fields.forEach((field) => {
      const id = fieldKeyFor(field);
      if (field.type === "checkbox") {
        values[id] = formData.get(id) === "on";
      } else {
        values[id] = formData.get(id) ?? "";
      }
    });

    return createFormulierInzending({
      data: values,
      formName: form.name,
      formulier: formulier.id,
    });
  };

  return (
    <section
      id={data.anchorId || undefined}
      className="py-16 md:py-32"
      style={{ backgroundColor: getBgColor(backgroundColor) }}
    >
      <div className="grid-container">
        <div className="col-span-12 md:col-span-8 md:col-start-3">
          {form.title && (
            <h2 className="header-3 mb-8 md:text-[42px]" style={{ color: getTextColorByBgColor(backgroundColor) }}>
              {form.title}
            </h2>
          )}
          {form.description && <RichText data={form.description} className="rich-text mb-8" />}
          <DynamicForm
            fields={dynamicFields}
            onSubmit={handleSubmit}
            buttonLabel={form.submitButtonText || "Versturen"}
            sendMessage={form.confirmationMessage || "Bedankt voor uw inzending."}
            textNotWhite={!isDarkBg}
          />
        </div>
      </div>
    </section>
  );
};

export default FormulierSection;
